import React, { useEffect, useMemo, useState } from "react";
import { Camera, Instagram, Mail, MessageCircle } from "lucide-react";

const ComingSoon = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  // Launch date - set to 30 days from now
  const launchDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date;
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = launchDate.getTime() - now;

      if (distance < 0) {
        clearInterval(timer);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      } else {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000),
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [launchDate]);

  return (
    <div
      className='min-h-screen relative overflow-hidden flex items-center justify-center'
      style={{ backgroundColor: "#efe4dc" }}>
      {/* Subtle Background Pattern */}
      <div className='absolute inset-0 z-0 opacity-5'>
        <div className='absolute top-20 left-20 w-64 h-64 rounded-full bg-gray-400 blur-3xl animate-pulse'></div>
        <div
          className='absolute bottom-20 right-20 w-96 h-96 rounded-full bg-gray-400 blur-3xl animate-pulse'
          style={{ animationDelay: "1s" }}></div>
        <div
          className='absolute top-1/2 left-1/2 w-80 h-80 rounded-full bg-gray-400 blur-3xl animate-pulse'
          style={{ animationDelay: "2s" }}></div>
      </div>

      {/* Main Content */}
      <div className='relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
        {/* Logo/Brand */}
        <div className='mb-8 animate-fadeIn'>
          <div className='flex items-center justify-center mb-4'>
            <div
              className='p-4 rounded-full'
              style={{ animation: "float 3s ease-in-out infinite", backgroundColor: "#514e46" }}>
              <Camera className='h-16 w-16 sm:h-20 sm:w-20 text-white' />
            </div>
          </div>
          <h1
            className='text-5xl sm:text-6xl lg:text-7xl font-light mb-3 tracking-wide'
            style={{ color: "#4a4a4a" }}>
            Jada Hester
          </h1>
          <div
            className='inline-block px-6 py-2 rounded-full text-sm sm:text-base font-medium tracking-widest'
            style={{ backgroundColor: "#514e46", color: "white" }}>
            PHOTOGRAPHY
          </div>
        </div>

        {/* Coming Soon Text */}
        <div className='mb-12 animate-slideUp'>
          <h2
            className='text-3xl sm:text-4xl lg:text-5xl font-light mb-4'
            style={{ color: "#4a4a4a" }}>
            Something Beautiful is
            <span
              className='block font-bold'
              style={{ color: "#514e46" }}>
              Coming Soon
            </span>
          </h2>
          <p
            className='text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed'
            style={{ color: "#5a5a5a" }}>
            I'm crafting a stunning new portfolio to showcase my work in family photography, portraits, and commercial shoots. Stay tuned for an inspiring
            visual journey.
          </p>
        </div>

        {/* Countdown Timer */}
        <div className='mb-12'>
          <div className='grid grid-cols-4 gap-3 sm:gap-6 max-w-2xl mx-auto'>
            {[
              { label: "Days", value: timeLeft.days },
              { label: "Hours", value: timeLeft.hours },
              { label: "Minutes", value: timeLeft.minutes },
              { label: "Seconds", value: timeLeft.seconds },
            ].map((item, index) => (
              <div
                key={item.label}
                className='rounded-lg p-4 sm:p-6 border'
                style={{
                  animation: `fadeIn 0.5s ease-out ${index * 0.1}s both`,
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                  backgroundColor: "white",
                  borderColor: "#d4c4b8",
                }}>
                <div
                  className='text-3xl sm:text-4xl lg:text-5xl font-bold mb-1'
                  style={{ color: "#514e46" }}>
                  {String(item.value).padStart(2, "0")}
                </div>
                <div
                  className='text-xs sm:text-sm uppercase tracking-wider'
                  style={{ color: "#6a6a6a" }}>
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Social Links */}
        <div className='mb-8'>
          <p
            className='mb-4 text-sm sm:text-base'
            style={{ color: "#5a5a5a" }}>
            Follow my journey
          </p>
          <div className='flex justify-center gap-6'>
            <a
              href='mailto:jada.hester@gmail.com'
              className='p-3 rounded-full border transition-all transform hover:scale-110'
              style={{ backgroundColor: "white", borderColor: "#d4c4b8" }}
              aria-label='Email'>
              <Mail
                className='h-6 w-6'
                style={{ color: "#514e46" }}
              />
            </a>
            <a
              href='https://instagram.com'
              target='_blank'
              rel='noopener noreferrer'
              className='p-3 rounded-full border transition-all transform hover:scale-110'
              style={{ backgroundColor: "white", borderColor: "#d4c4b8" }}
              aria-label='Instagram'>
              <Instagram
                className='h-6 w-6'
                style={{ color: "#514e46" }}
              />
            </a>
            <button
              onClick={() => (window.location.href = "mailto:jada.hester@gmail.com")}
              className='p-3 rounded-full border transition-all transform hover:scale-110'
              style={{ backgroundColor: "white", borderColor: "#d4c4b8" }}
              aria-label='Contact'>
              <MessageCircle
                className='h-6 w-6'
                style={{ color: "#514e46" }}
              />
            </button>
          </div>
        </div>

        {/* Footer Text */}
        <div
          className='text-sm'
          style={{ color: "#7a7a7a" }}>
          <p>Kansas City Metropolitan Area</p>
          <p className='mt-2'>© 2025 Jada Hester Photography. All rights reserved.</p>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 1s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.8s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ComingSoon;
